import type * as React from 'react'
import { CheckCircle2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getJSON } from '@/lib/api'
import { PublicJoinShell } from '@/pages/PublicJoinFormPage.parts'
import type {
  CreatedJoinApplicationResponse,
  JoinApplicationResponse,
  JoinPaymentConfirmationResponse,
} from '@/pages/PublicJoinFormPage.types'
import type { Association } from '@/pages/StaffSignInPage'

export function PublicJoinPaymentConfirmation({
  association,
  paymentToken,
  sessionId,
}: {
  association: Association
  paymentToken: string
  sessionId: string
}) {
  const query = useQuery({
    queryKey: ['public-join-payment-confirmation', paymentToken, sessionId],
    queryFn: () =>
      getJSON<JoinPaymentConfirmationResponse>(
        `/api/v1/join/payment_confirmation?token=${encodeURIComponent(paymentToken)}&session_id=${encodeURIComponent(sessionId)}`,
      ),
    retry: false,
    refetchInterval: (result) =>
      result.state.data?.confirmation?.status === 'pending' ? 1000 : false,
  })

  return (
    <PublicJoinShell association={association}>
      {query.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Confirming payment...
        </p>
      ) : query.isError || !query.data ? (
        <Alert>Payment confirmation could not be loaded.</Alert>
      ) : (
        <PaymentConfirmationPanel confirmation={query.data} />
      )}
    </PublicJoinShell>
  )
}

function PaymentConfirmationPanel({
  confirmation,
}: {
  confirmation: JoinPaymentConfirmationResponse
}) {
  if (confirmation.confirmation.status === 'pending') {
    return (
      <ConfirmationCard>
        <div>
          <h1 className="text-subheading font-semibold text-foreground">Confirming payment</h1>
          <p className="mt-1 text-body-sm text-muted-foreground">
            Your payment confirmation is still on its way.
          </p>
        </div>
      </ConfirmationCard>
    )
  }

  return <MembershipConfirmation membership={confirmation.membership} />
}

export function ConfirmationPanel({ submission }: { submission: JoinApplicationResponse }) {
  if (!('membership' in submission)) return <ReceivedConfirmation />

  if (submission.payment?.mode === 'hosted_checkout' && submission.payment.checkout_url) {
    return <HostedCheckoutConfirmation submission={submission} />
  }

  return <MembershipConfirmation membership={submission.membership} payment={submission.payment} />
}

function MembershipConfirmation({
  membership,
  payment,
}: {
  membership: CreatedJoinApplicationResponse['membership']
  payment?: CreatedJoinApplicationResponse['payment']
}) {
  const isActive = membership.status === 'Active'

  return (
    <ConfirmationCard>
      <div>
        <h1 className="text-subheading font-semibold text-foreground">
          {isActive ? 'You are now a member' : 'Application received'}
        </h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          {isActive
            ? `${membership.level} membership is active.`
            : `${membership.level} is awaiting staff approval.`}
        </p>
      </div>
      {isActive && membership.member_number && (
        <Badge variant="success">Member number {membership.member_number}</Badge>
      )}
      {payment?.mode === 'offline' && (
        <div className="space-y-2">
          {payment.unavailable_reason && <Alert>{payment.unavailable_reason}</Alert>}
          {payment.offline_instructions && (
            <p className="text-body-sm text-muted-foreground">{payment.offline_instructions}</p>
          )}
        </div>
      )}
    </ConfirmationCard>
  )
}

function ReceivedConfirmation() {
  return (
    <ConfirmationCard>
      <div>
        <h1 className="text-subheading font-semibold text-foreground">Application received</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          The association has received your application.
        </p>
      </div>
    </ConfirmationCard>
  )
}

function HostedCheckoutConfirmation({
  submission,
}: {
  submission: Extract<JoinApplicationResponse, { membership: unknown }>
}) {
  const payment = submission.payment!

  return (
    <ConfirmationCard>
      <div>
        <h1 className="text-subheading font-semibold text-foreground">Complete your payment</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Pay {payment.amount} securely through Stripe.
        </p>
      </div>
      <Button asChild>
        <a href={payment.checkout_url!}>Continue to secure payment</a>
      </Button>
    </ConfirmationCard>
  )
}

function ConfirmationCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-md border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" strokeWidth={1.8} />
        <div className="min-w-0 flex-1 space-y-4">{children}</div>
      </div>
    </section>
  )
}
